import { useState } from 'react'
import { useWallet } from '../contexts/WalletContext'
import {
  getAdminAddress,
  useAdminLogin,
  usePendingReports,
  useInvalidateProof,
  useAddWarning,
  useRejectReport,
  useCreatorWarnings,
  useRevokeCreator,
  useVerifiedApplications,
  useGrantVerifiedCreator,
  useRejectApplication,
  WARNING_THRESHOLD,
} from '../hooks/useAdminDashboard'
import type { EscalatedReport, CreatorWarning } from '../hooks/useAdminDashboard'
import { formatAddress } from '../types/challenge'

function LoginSection({
  onLogin,
  isAuthenticating,
  authError,
}: {
  onLogin: () => void
  isAuthenticating: boolean
  authError: string | null
}) {
  return (
    <div
      className="card card-pad"
      style={{ textAlign: 'center', padding: '48px 24px' }}
      data-testid="admin-login-section"
    >
      <div style={{ fontSize: 36, marginBottom: 14 }}>🔐</div>
      <p style={{ margin: '0 0 8px', fontWeight: 700, fontSize: 17 }}>관리자 인증이 필요합니다</p>
      <p style={{ margin: '0 0 22px', fontSize: 14, color: 'var(--ink-2)' }}>
        MetaMask로 메시지에 서명하여 관리자 권한을 확인합니다.
      </p>
      <button
        className="btn btn-primary"
        onClick={onLogin}
        disabled={isAuthenticating}
        data-testid="admin-login-btn"
      >
        {isAuthenticating ? '인증 중…' : '관리자 인증하기'}
      </button>
      {authError && (
        <div className="alert alert-danger" role="alert" style={{ marginTop: 12 }}>
          {authError}
        </div>
      )}
    </div>
  )
}

function ReportDetailModal({
  report,
  onInvalidate,
  onReject,
  onWarn,
  onClose,
  isInvalidatePending,
  isRejectPending,
  isWarnPending,
  actionError,
}: {
  report: EscalatedReport
  onInvalidate: () => void
  onReject: () => void
  onWarn: () => void
  onClose: () => void
  isInvalidatePending: boolean
  isRejectPending: boolean
  isWarnPending: boolean
  actionError: string | null
}) {
  return (
    <div className="modal-scrim" onClick={onClose} data-testid="report-detail-modal">
      <div className="modal wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3 className="modal-title">신고 상세 — #{report.reportId}</h3>
          <button className="btn btn-quiet btn-sm" onClick={onClose} data-testid="modal-close-btn">
            ✕
          </button>
        </div>
        <div className="modal-body">
          <div className="info-rows">
            <div className="info-row">
              <span className="k">챌린지</span>
              <span className="v mono">{formatAddress(report.challengeAddress)}</span>
            </div>
            <div className="info-row">
              <span className="k">신고자</span>
              <span className="v mono">{formatAddress(report.reporter)}</span>
            </div>
            <div className="info-row">
              <span className="k">신고 대상</span>
              <span className="v mono">{formatAddress(report.target)}</span>
            </div>
            <div className="info-row">
              <span className="k">라운드</span>
              <span className="v">{report.round + 1}라운드</span>
            </div>
            <div className="info-row">
              <span className="k">사유</span>
              <span className="v">
                {report.reason}
                {report.reasonDetail ? ` — ${report.reasonDetail}` : ''}
              </span>
            </div>
            <div className="info-row">
              <span className="k">신고 일시</span>
              <span className="v">{new Date(report.createdAt).toLocaleString('ko-KR')}</span>
            </div>
          </div>
          {actionError && (
            <div className="alert alert-danger" role="alert">
              {actionError}
            </div>
          )}
          <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 10 }}>
            무효화하면 대상의 해당 인증이 무효 처리되어 <strong>탈락</strong>합니다(온체인). 정당하지
            않은 신고면 기각하세요.
          </p>
        </div>
        <div className="modal-foot">
          <button
            className="btn btn-subtle btn-sm"
            onClick={onWarn}
            disabled={isWarnPending}
            data-testid={`warn-btn-${report.reportId}`}
          >
            {isWarnPending ? '처리 중…' : '대상 경고'}
          </button>
          <button
            className="btn btn-ghost"
            onClick={onReject}
            disabled={isRejectPending}
            data-testid={`reject-btn-${report.reportId}`}
          >
            {isRejectPending ? '처리 중…' : '기각'}
          </button>
          <button
            className="btn btn-danger"
            onClick={onInvalidate}
            disabled={isInvalidatePending}
            data-testid={`invalidate-btn-${report.reportId}`}
          >
            {isInvalidatePending ? '처리 중…' : '인증 무효화 (탈락)'}
          </button>
        </div>
      </div>
    </div>
  )
}

function PendingReportsTab({ jwt }: { jwt: string }) {
  const { data, isLoading } = usePendingReports(jwt)
  const invalidate = useInvalidateProof(jwt)
  const rejectReport = useRejectReport(jwt)
  const addWarning = useAddWarning(jwt)
  const [selectedReport, setSelectedReport] = useState<EscalatedReport | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const reports = data?.reports ?? []

  const handleInvalidate = () => {
    if (!selectedReport) return
    setActionError(null)
    invalidate.mutate(
      {
        reportId: selectedReport.reportId,
        challengeAddress: selectedReport.challengeAddress,
        target: selectedReport.target,
        round: selectedReport.round,
      },
      {
        onError: (err) => setActionError(err.message),
        onSuccess: () => setSelectedReport(null),
      },
    )
  }
  const handleReject = () => {
    if (!selectedReport) return
    setActionError(null)
    rejectReport.mutate(selectedReport.reportId, {
      onError: (err) => setActionError(err.message),
      onSuccess: () => setSelectedReport(null),
    })
  }
  const handleWarn = () => {
    if (!selectedReport) return
    setActionError(null)
    addWarning.mutate(selectedReport.target, {
      onError: (err) => setActionError(err.message),
    })
  }

  if (isLoading)
    return (
      <div className="empty" data-testid="reports-loading">
        <div className="spinner" />
        <p>신고 목록을 불러오는 중…</p>
      </div>
    )

  return (
    <div data-testid="tab-content-reports">
      {reports.length === 0 ? (
        <div className="empty" data-testid="no-reports">
          <p>처리할 신고가 없습니다.</p>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table className="table">
            <thead>
              <tr>
                <th>신고 ID</th>
                <th>챌린지</th>
                <th>신고 대상</th>
                <th>사유</th>
                <th>신고 일시</th>
                <th>액션</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                <tr
                  key={report.reportId}
                  data-testid={`report-row-${report.reportId}`}
                  className="clickable"
                >
                  <td>
                    <span className="mono">#{report.reportId.slice(0, 8)}</span>
                  </td>
                  <td>
                    <span className="mono">{formatAddress(report.challengeAddress)}</span>
                  </td>
                  <td>
                    <span className="mono">{formatAddress(report.target)}</span>
                  </td>
                  <td style={{ maxWidth: 200 }}>{report.reason}</td>
                  <td>{new Date(report.createdAt).toLocaleDateString('ko-KR')}</td>
                  <td>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => setSelectedReport(report)}
                      data-testid={`report-detail-btn-${report.reportId}`}
                    >
                      처리하기
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {selectedReport && (
        <ReportDetailModal
          report={selectedReport}
          onInvalidate={handleInvalidate}
          onReject={handleReject}
          onWarn={handleWarn}
          onClose={() => setSelectedReport(null)}
          isInvalidatePending={invalidate.isPending}
          isRejectPending={rejectReport.isPending}
          isWarnPending={addWarning.isPending}
          actionError={actionError}
        />
      )}
    </div>
  )
}

function RevokeConfirmModal({
  creator,
  onConfirm,
  onClose,
  isPending,
  error,
}: {
  creator: CreatorWarning
  onConfirm: () => void
  onClose: () => void
  isPending: boolean
  error: string | null
}) {
  return (
    <div className="modal-scrim" onClick={onClose} data-testid="revoke-confirm-modal">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3 className="modal-title" style={{ color: 'var(--danger)' }}>
            ⚠️ 개설자 권한 박탈
          </h3>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 14, margin: 0 }}>다음 개설자의 챌린지 개설 권한을 박탈합니다:</p>
          <div
            className="mono"
            style={{
              background: 'var(--surface-3)',
              padding: '8px 12px',
              borderRadius: 'var(--radius-sm)',
              fontSize: 13,
              marginTop: 8,
            }}
          >
            {creator.address}
          </div>
          <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: 0 }}>
            이 작업은 온체인 트랜잭션으로 처리되며 되돌릴 수 없습니다.
          </p>
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose} data-testid="revoke-modal-cancel">
            취소
          </button>
          <button
            className="btn btn-danger"
            onClick={onConfirm}
            disabled={isPending}
            data-testid="revoke-modal-confirm"
          >
            {isPending ? '처리 중…' : '권한 박탈 확인'}
          </button>
        </div>
      </div>
    </div>
  )
}

function CreatorWarningsTab() {
  const { data: creators, isLoading } = useCreatorWarnings()
  const revokeCreator = useRevokeCreator()
  const [revokeTarget, setRevokeTarget] = useState<CreatorWarning | null>(null)
  const [revokeError, setRevokeError] = useState<string | null>(null)

  const handleRevoke = () => {
    if (!revokeTarget) return
    setRevokeError(null)
    revokeCreator.mutate(revokeTarget.address, {
      onError: (err) => setRevokeError(err.message),
      onSuccess: () => setRevokeTarget(null),
    })
  }

  if (isLoading)
    return (
      <div className="empty" data-testid="warnings-loading">
        <div className="spinner" />
      </div>
    )
  const list = creators ?? []

  return (
    <div data-testid="tab-content-warnings">
      {list.length === 0 ? (
        <div className="empty" data-testid="no-warnings">
          <p>경고가 부여된 개설자가 없습니다.</p>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table className="table">
            <thead>
              <tr>
                <th>개설자 주소</th>
                <th>경고 횟수</th>
                <th>최근 경고</th>
                <th>상태</th>
                <th>액션</th>
              </tr>
            </thead>
            <tbody>
              {list.map((creator) => {
                const canRevoke = creator.count >= WARNING_THRESHOLD && !creator.isRevoked
                return (
                  <tr key={creator.address} data-testid={`creator-row-${creator.address}`}>
                    <td>
                      <span className="mono">{formatAddress(creator.address)}</span>
                    </td>
                    <td>
                      {creator.count} / {WARNING_THRESHOLD}
                    </td>
                    <td>
                      {creator.lastWarningAt
                        ? new Date(creator.lastWarningAt).toLocaleDateString('ko-KR')
                        : '-'}
                    </td>
                    <td>
                      {creator.isRevoked ? (
                        <span className="badge badge-neutral">박탈됨</span>
                      ) : creator.count >= WARNING_THRESHOLD ? (
                        <span className="badge badge-danger">⚠ 임박</span>
                      ) : (
                        <span className="badge badge-warning">경고 중</span>
                      )}
                    </td>
                    <td>
                      {canRevoke && (
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => setRevokeTarget(creator)}
                          data-testid={`revoke-btn-${creator.address}`}
                        >
                          권한 박탈
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {revokeTarget && (
        <RevokeConfirmModal
          creator={revokeTarget}
          onConfirm={handleRevoke}
          onClose={() => setRevokeTarget(null)}
          isPending={revokeCreator.isPending}
          error={revokeError}
        />
      )}
    </div>
  )
}

function VerifiedApplicationsTab({ jwt }: { jwt: string }) {
  const { data, isLoading } = useVerifiedApplications(jwt)
  const grantVerified = useGrantVerifiedCreator()
  const rejectApplication = useRejectApplication(jwt)
  const [actionError, setActionError] = useState<string | null>(null)

  const handleGrant = (addr: string) => {
    setActionError(null)
    grantVerified.mutate(addr, { onError: (err) => setActionError(err.message) })
  }
  const handleReject = (addr: string) => {
    setActionError(null)
    rejectApplication.mutate({ address: addr }, { onError: (err) => setActionError(err.message) })
  }

  if (isLoading)
    return (
      <div className="empty" data-testid="applications-loading">
        <div className="spinner" />
      </div>
    )
  const applications = (data?.applications ?? []).filter((a) => a.status === 'pending')

  return (
    <div data-testid="tab-content-applications">
      {actionError && (
        <div className="alert alert-danger" role="alert" style={{ marginBottom: 12 }}>
          {actionError}
        </div>
      )}
      {applications.length === 0 ? (
        <div className="empty" data-testid="no-applications">
          <p>인증 개설자 자격 신청이 없습니다.</p>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table className="table">
            <thead>
              <tr>
                <th>신청자</th>
                <th>신청 사유</th>
                <th>신청 일자</th>
                <th>액션</th>
              </tr>
            </thead>
            <tbody>
              {applications.map((app) => (
                <tr key={app.address} data-testid={`application-row-${app.address}`}>
                  <td>
                    <span className="mono">{formatAddress(app.address)}</span>
                  </td>
                  <td style={{ maxWidth: 260 }}>
                    <span
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {app.reason}
                    </span>
                  </td>
                  <td>{new Date(app.createdAt).toLocaleDateString('ko-KR')}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleGrant(app.address)}
                        disabled={grantVerified.isPending}
                        data-testid={`grant-btn-${app.address}`}
                      >
                        자격 부여
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => handleReject(app.address)}
                        disabled={rejectApplication.isPending}
                        data-testid={`reject-btn-${app.address}`}
                      >
                        거절
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

type TabId = 'reports' | 'warnings' | 'applications'

export default function AdminDashboard() {
  const { address: userAddress, isConnected } = useWallet()
  const [activeTab, setActiveTab] = useState<TabId>('reports')
  const { jwt, isAuthenticating, authError, login } = useAdminLogin()

  const adminAddress = getAdminAddress()
  const isAdmin =
    isConnected && userAddress !== null && adminAddress !== '' && userAddress === adminAddress

  if (!isConnected || !isAdmin) {
    return (
      <div className="access-denied" data-testid="access-denied">
        <span style={{ fontSize: 40 }}>🚫</span>
        <h2>접근 권한이 없습니다</h2>
        <p>관리자 지갑으로 연결해 주세요.</p>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div
        className="card card-pad"
        style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: 'var(--primary-weak)',
            display: 'grid',
            placeItems: 'center',
            fontSize: 22,
          }}
        >
          🛡️
        </div>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700 }}>관리자 대시보드</div>
          <div className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            {userAddress}
          </div>
        </div>
      </div>

      {!jwt ? (
        <LoginSection onLogin={login} isAuthenticating={isAuthenticating} authError={authError} />
      ) : (
        <>
          <div className="tabs">
            <button
              className={`tab-btn${activeTab === 'reports' ? ' active' : ''}`}
              onClick={() => setActiveTab('reports')}
              data-testid="tab-reports"
            >
              신고 처리
            </button>
            <button
              className={`tab-btn${activeTab === 'warnings' ? ' active' : ''}`}
              onClick={() => setActiveTab('warnings')}
              data-testid="tab-warnings"
            >
              개설자 경고 현황
            </button>
            <button
              className={`tab-btn${activeTab === 'applications' ? ' active' : ''}`}
              onClick={() => setActiveTab('applications')}
              data-testid="tab-applications"
            >
              인증 개설자 자격
            </button>
          </div>
          {activeTab === 'reports' && <PendingReportsTab jwt={jwt} />}
          {activeTab === 'warnings' && <CreatorWarningsTab />}
          {activeTab === 'applications' && <VerifiedApplicationsTab jwt={jwt} />}
        </>
      )}
    </div>
  )
}
